// Gateway client for My Pay. Auth is the httpOnly session cookie (withCredentials)
// with attachCookieAuth echoing the CSRF token on writes — on a dedicated instance
// that does not force logout on error (same pattern as the payroll operator UI).
import axios from 'axios';
import { apiConfig } from '@/config/api.config';
import type {
  PayslipSummary,
  PayslipDetail,
  CategoryGroup,
  ComponentType,
  YtdSummary,
  TrendSeries,
  PaymentInfo,
  MyCompensation,
  CompensationEntry,
  IncomeStatement,
} from '../types';
import { humanizePeriod } from '../lib/format';
import { attachCookieAuth } from '@/lib/session';

const API = '/api/v1';
const num = (s: string | number | undefined | null): number => (s == null ? 0 : Number(s));

const http = axios.create({ baseURL: apiConfig.baseURL, timeout: 20000, withCredentials: true });
http.interceptors.request.use(attachCookieAuth);

/**
 * True when a My Pay SELF read was refused because the signed-in account has no
 * employee record linked on the payroll side — 404 { code: 'not_found', message: 'no
 * employee record is linked to your account yet' }. That is a set-up state HR fixes,
 * not an outage (Retry can never help) and not "no payslips yet".
 *
 * ⚠️ Only meaningful for the self endpoints (/payslips, /payslips/ytd, /payslips/analytics,
 * /me/compensation, /me/pay-schedule), where a 404 can only mean "who are you?". A 404
 * from /payslips/{id} is a missing payslip — don't pass those here. Every other status
 * (403, 5xx, no response) stays an error.
 */
export function isEmployeeNotLinked(e: unknown): boolean {
  const res = (e as { response?: { status?: number; data?: { code?: unknown; message?: unknown } } } | null)?.response;
  if (res?.status !== 404) return false;
  const code = typeof res.data?.code === 'string' ? res.data.code : '';
  const message = typeof res.data?.message === 'string' ? res.data.message : '';
  // A different explicit code is a different 404 (e.g. a route that doesn't exist on this
  // gateway) — only the generic not_found, or a body that says so, means "not linked".
  if (code && code !== 'not_found' && !/employee record/i.test(message)) return false;
  return true;
}

/** react-query retry predicate for the self reads: a not-linked 404 is never retried. */
export const retryUnlessNotLinked = (count: number, err: unknown) => !isEmployeeNotLinked(err) && count < 3;

export async function listMyPayslips(employeeId: string): Promise<PayslipSummary[]> {
  const { data } = await http.get<{ payslips: any[] }>(`${API}/payslips`, {
    params: { employee_id: employeeId },
  });
  return (data.payslips ?? []).map(
    (p): PayslipSummary => ({
      id: p.id,
      rawPeriod: p.period_label ?? '',
      periodLabel: humanizePeriod(p.period_label ?? ''),
      payDate: p.generated_at ?? null,
      status: p.status ?? 'pending',
      gross: num(p.gross),
      net: num(p.net),
      currency: p.currency || 'TZS',
    }),
  );
}

export async function getMyAnalytics(employeeId: string, months: number): Promise<TrendSeries> {
  const { data } = await http.get<any>(`${API}/payslips/analytics`, {
    params: { employee_id: employeeId, months },
  });
  return {
    currency: data.currency || 'TZS',
    points: (data.points ?? []).map((p: any) => ({
      periodLabel: humanizePeriod(p.period_label),
      periodEnd: p.period_end,
      gross: num(p.gross),
      net: num(p.net),
      byType: Object.fromEntries(
        Object.entries(p.by_type ?? {}).map(([k, v]) => [k, num(v as any)]),
      ) as TrendSeries['points'][number]['byType'],
    })),
  };
}

export async function getMyYtd(employeeId: string, year: number): Promise<YtdSummary> {
  const { data } = await http.get<any>(`${API}/payslips/ytd`, {
    params: { employee_id: employeeId, year },
  });
  const categories: CategoryGroup[] = (data.categories ?? []).map((c: any) => ({
    type: c.type as ComponentType,
    total: num(c.total),
    items: (c.items ?? []).map((it: any) => ({
      code: it.code,
      label: it.label,
      amount: num(it.amount),
      componentType: it.component_type as ComponentType,
      explanation: it.explanation || undefined,
    })),
  }));
  return {
    year: data.year,
    currency: data.currency || 'TZS',
    gross: num(data.gross),
    net: num(data.net),
    totalTaxes: num(data.total_taxes),
    totalDeductions: num(data.total_deductions),
    totalEmployerContributions: num(data.total_employer_contributions),
    categories,
  };
}

export async function getMyPayslip(id: string): Promise<PayslipDetail> {
  const { data } = await http.get<any>(`${API}/payslips/${id}`);
  const categories: CategoryGroup[] = (data.categories ?? []).map((c: any) => ({
    type: c.type as ComponentType,
    total: num(c.total),
    items: (c.items ?? []).map((it: any) => ({
      code: it.code,
      label: it.label,
      amount: num(it.amount),
      componentType: it.component_type as ComponentType,
      explanation: it.explanation || undefined,
    })),
  }));
  return {
    id: data.id,
    periodLabel: humanizePeriod(data.period_label ?? ''),
    payPeriod: data.pay_period ?? '',
    status: data.status ?? 'pending',
    country: data.country ?? '',
    currency: data.currency || 'TZS',
    gross: num(data.gross),
    taxableIncome: num(data.taxable_income),
    totalDeductions: num(data.total_deductions),
    totalEmployerContributions: num(data.total_employer_contributions),
    net: num(data.net),
    categories,
    downloadUrl: data.download_url || undefined,
    payment: mapPayment(data.payment),
  };
}

export function mapPayment(p: any): PaymentInfo | undefined {
  if (!p) return undefined;
  const info: PaymentInfo = {
    channel: p.channel ?? '',
    provider: p.provider ?? '',
    destinationMasked: p.destination_masked ?? '',
    reference: p.reference ?? '',
    status: p.status ?? 'pending',
    submittedAt: p.submitted_at || undefined,
    settledAt: p.settled_at || undefined,
  };
  if (Array.isArray(p.payments) && p.payments.length > 1) {
    info.shares = p.payments.map((s: any) => ({
      seq: Number(s.seq ?? 0),
      amount: Number(s.amount ?? 0),
      channel: s.channel ?? '',
      provider: s.provider ?? '',
      destinationMasked: s.destination_masked ?? '',
      reference: s.reference ?? '',
      status: s.status ?? 'pending',
    }));
  }
  return info;
}

// --- Phase 6: compensation (self) -----------------------------------------

/** Thrown as-is on 403 disabled_by_policy so callers can hide the tab. */
export const COMPENSATION_DISABLED = 'disabled_by_policy';

function mapCompensation(c: any): CompensationEntry {
  return {
    id: c.id,
    type: c.type ?? 'base',
    amount: num(c.amount),
    currency: c.currency || 'TZS',
    effectiveFrom: c.effective_from ?? '',
    effectiveTo: c.effective_to || undefined,
    status: c.status ?? 'active',
  };
}

export async function getMyCompensation(): Promise<MyCompensation> {
  try {
    const { data } = await http.get<any>(`${API}/me/compensation`);
    return {
      current: data.current ? mapCompensation(data.current) : undefined,
      history: (data.history ?? []).map(mapCompensation),
    };
  } catch (e: any) {
    if (e?.response?.status === 403 && e?.response?.data?.code === COMPENSATION_DISABLED) {
      throw new Error(COMPENSATION_DISABLED);
    }
    throw e;
  }
}

// --- Phase 5: income statement (self, server-rendered) ---------------------

export async function getIncomeStatement(from: string, to: string): Promise<IncomeStatement> {
  const { data } = await http.get<any>(`${API}/me/income-statement`, { params: { from, to } });
  return {
    from: data.from,
    to: data.to,
    currency: data.currency || 'TZS',
    gross: num(data.gross),
    net: num(data.net),
    periods: (data.periods ?? []).map((p: any) => ({
      periodLabel: humanizePeriod(p.period_label),
      periodEnd: p.period_end,
      gross: num(p.gross),
      net: num(p.net),
      byType: Object.fromEntries(Object.entries(p.by_type ?? {}).map(([k, v]) => [k, num(v as any)])),
    })),
    lines: (data.lines ?? []).map((l: any) => ({
      code: l.code,
      label: l.label,
      amount: num(l.amount),
      componentType: l.type as ComponentType,
    })),
  };
}

/** Download the server-rendered statement (branded PDF or CSV) via the auth token. */
export async function downloadIncomeStatement(from: string, to: string, format: 'pdf' | 'csv'): Promise<void> {
  const resp = await http.get(`${API}/me/income-statement`, {
    params: { from, to, format },
    responseType: 'blob',
  });
  const url = URL.createObjectURL(resp.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `income-statement-${from}-to-${to}.${format}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
